import type { WebsiteHitRow } from 'aevocam-rest-api-spec'
import { asLocalDate } from '@/utilities/formatters'
import {
  estimatedDailyAverage,
  estimatedHourlyAverage,
  startOfDayInTimeZone,
  startOfHourInTimeZone,
  TELEMETRY_TIME_ZONE,
} from '../telemetry/telemetryHelpers'

export const WEB_HIT_TIME_ZONE = TELEMETRY_TIME_ZONE
export const WEB_HIT_HOURLY_PERIOD_SEC = 3600
export const WEB_HIT_DAILY_PERIOD_SEC = 86400
export const WEB_HIT_HOURLY_BUCKET_COUNT = 3 * 24
export const WEB_HIT_DAILY_BUCKET_COUNT = 90
export const WEB_HIT_REFRESH_MS = 10_000

const HOUR_MS = WEB_HIT_HOURLY_PERIOD_SEC * 1000
const DAY_MS = WEB_HIT_DAILY_PERIOD_SEC * 1000

export type WebHitCard = {
  key: string
  label: string
  bucketFrom: Date
  buckets: number[]
  hourlyAverage: number
  hourlyDurationAvgMs?: number
  hourlyDurationMaxMs?: number
  dailyBucketFrom: Date
  dailyBuckets: number[]
  dailyAverage: number
  dailyDurationAvgMs?: number
  dailyDurationMaxMs?: number
}

export function formatDurationMs(ms: number | undefined): string | undefined {
  if (ms == null) return undefined
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toLocaleString('en-US', { maximumFractionDigits: 2 })} s`
}

export function formatWebHitLabel(path: string): string {
  return path || '/'
}

export function webHitHourlyWindow(now = new Date()) {
  const currentHourStart = startOfHourInTimeZone(now, WEB_HIT_TIME_ZONE)
  const from = new Date(
    currentHourStart.getTime() - (WEB_HIT_HOURLY_BUCKET_COUNT - 1) * HOUR_MS,
  )
  const to = new Date(currentHourStart.getTime() + HOUR_MS)
  return { from, to, now }
}

export function webHitDailyWindow(now = new Date()) {
  const currentDayStart = startOfDayInTimeZone(now, WEB_HIT_TIME_ZONE)
  const from = new Date(
    currentDayStart.getTime() - (WEB_HIT_DAILY_BUCKET_COUNT - 1) * DAY_MS,
  )
  const to = new Date(currentDayStart.getTime() + DAY_MS)
  return { from, to, now }
}

export function buildWebHitTimeSeries(
  rows: WebsiteHitRow[],
  from: Date,
  to: Date,
  periodSec: number,
): number[] {
  const periodMs = periodSec * 1000
  const slotCount = Math.ceil((to.getTime() - from.getTime()) / periodMs)
  const buckets = new Array<number>(slotCount).fill(0)
  const fromMs = from.getTime()

  for (const row of rows) {
    if (row.createdAt == null || row.count == null) continue
    const idx = Math.floor((new Date(row.createdAt).getTime() - fromMs) / periodMs)
    if (idx >= 0 && idx < slotCount) {
      buckets[idx] = (buckets[idx] ?? 0) + row.count
    }
  }

  return buckets
}

function groupRowsByPath(rows: WebsiteHitRow[]): Map<string, WebsiteHitRow[]> {
  const grouped = new Map<string, WebsiteHitRow[]>()
  for (const row of rows) {
    const path = row.path ?? ''
    const group = grouped.get(path) ?? []
    group.push(row)
    grouped.set(path, group)
  }
  return grouped
}

type DurationStats = {
  avg?: number
  max?: number
}

function durationStatsByPath(rows: WebsiteHitRow[]): Map<string, DurationStats> {
  const map = new Map<string, DurationStats>()
  for (const row of rows) {
    const path = row.path ?? ''
    map.set(path, {
      avg: row.durationAvg,
      max: row.durationMax,
    })
  }
  return map
}

export function rowsToWebHitCards(
  hourlyRows: WebsiteHitRow[],
  dailyRows: WebsiteHitRow[],
  hourlyDurationRows: WebsiteHitRow[],
  dailyDurationRows: WebsiteHitRow[],
  hourlyFrom: Date,
  hourlyTo: Date,
  dailyFrom: Date,
  dailyTo: Date,
  now: Date,
): WebHitCard[] {
  const hourlyByPath = groupRowsByPath(hourlyRows)
  const dailyByPath = groupRowsByPath(dailyRows)
  const hourlyDurationByPath = durationStatsByPath(hourlyDurationRows)
  const dailyDurationByPath = durationStatsByPath(dailyDurationRows)
  const paths = [...new Set([
    ...hourlyByPath.keys(),
    ...dailyByPath.keys(),
    ...hourlyDurationByPath.keys(),
    ...dailyDurationByPath.keys(),
  ])]

  return paths.map(path => {
    const hourlyDuration = hourlyDurationByPath.get(path)
    const dailyDuration = dailyDurationByPath.get(path)
    const buckets = buildWebHitTimeSeries(
      hourlyByPath.get(path) ?? [],
      hourlyFrom,
      hourlyTo,
      WEB_HIT_HOURLY_PERIOD_SEC,
    )
    const dailyBuckets = buildWebHitTimeSeries(
      dailyByPath.get(path) ?? [],
      dailyFrom,
      dailyTo,
      WEB_HIT_DAILY_PERIOD_SEC,
    )

    return {
      key: path,
      label: formatWebHitLabel(path),
      bucketFrom: hourlyFrom,
      buckets,
      hourlyAverage: estimatedHourlyAverage(buckets, now),
      hourlyDurationAvgMs: hourlyDuration?.avg,
      hourlyDurationMaxMs: hourlyDuration?.max,
      dailyBucketFrom: dailyFrom,
      dailyBuckets,
      dailyAverage: estimatedDailyAverage(dailyBuckets, now),
      dailyDurationAvgMs: dailyDuration?.avg,
      dailyDurationMaxMs: dailyDuration?.max,
    }
  }).sort((a, b) =>
    a.label.localeCompare(b.label),
  )
}

//--------------------------------------------------------------------------------
// Web hit list page (filtered, paged rows + daily sparkline + stats)

/** Filter inputs for the web hit list page */
export type WebHitListFilters = {
  /** Inclusive start day (yyyy-mm-dd) */
  fromDate: string
  /** Inclusive end day (yyyy-mm-dd) */
  toDate: string
  path: string
  query: string
  address: string
  agent: string
  /** Numeric HTTP status (blank for any) */
  status: string
  /** Minimum duration in milliseconds (blank for any) */
  duration: string
}

/** Aggregated summary shown above the matching rows */
export type WebHitListSummary = {
  totalRows: number
  durationMinMs?: number
  durationMaxMs?: number
  durationAvgMs?: number
  bucketFrom: Date
  dailyBuckets: number[]
}

/** yyyy-mm-dd string for a Date's local calendar day (for `<input type='date'>`) */
export function toDateInputValue(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Default page size for the web hit list grid */
export const WEB_HIT_DEFAULT_PAGE_SIZE = 10

/** Default filters: last 12 months */
export function defaultWebHitListFilters(now = new Date()): WebHitListFilters {
  const from = new Date(now)
  from.setMonth(from.getMonth() - 12)
  return {
    fromDate: toDateInputValue(from),
    toDate: toDateInputValue(now),
    path: '',
    query: '',
    address: '',
    agent: '',
    status: '',
    duration: '',
  }
}

/**
 * Turn a free-text filter into an ILIKE pattern list. A blank value yields
 * `undefined` (no filter). Use `*` in the value for SQL-style wildcards (eg
 * `/pricing*` for begins-with, `*Mozilla*` for contains); otherwise the value
 * is passed through as an exact match.
 */
export function toWildcardFilter(value: string): string[] | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return [trimmed]
}

/** Parse the numeric status filter into a single-element list (or undefined) */
export function parseStatusFilter(value: string): number[] | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? [Math.trunc(parsed)] : undefined
}

/** Parse minimum duration (ms) for durationGte, or undefined if blank/invalid */
export function parseDurationGteFilter(value: string): number | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? Math.max(0, parsed) : undefined
}

/**
 * Resolve the selected day range into query bounds. `from` is the start of the
 * `fromDate` day; `to` is the exclusive start of the day after `toDate`, so the
 * whole `toDate` day is included.
 */
export function webHitDateRange(fromDate: string, toDate: string, now = new Date()) {
  const from = startOfDayInTimeZone(asLocalDate(fromDate) ?? now, WEB_HIT_TIME_ZONE)
  const toDayStart = startOfDayInTimeZone(asLocalDate(toDate) ?? now, WEB_HIT_TIME_ZONE)
  const to = new Date(toDayStart.getTime() + DAY_MS)
  return { from, to }
}

/** Shared criteria (filters + time window) applied to every list-page query */
export function webHitListCriteria(filters: WebHitListFilters) {
  const { from, to } = webHitDateRange(filters.fromDate, filters.toDate)
  return {
    from,
    to,
    common: {
      path: toWildcardFilter(filters.path),
      query: toWildcardFilter(filters.query),
      remoteAddress: toWildcardFilter(filters.address),
      userAgent: toWildcardFilter(filters.agent),
      status: parseStatusFilter(filters.status),
      durationGte: parseDurationGteFilter(filters.duration),
      createdAtFrom: from.toISOString(),
      createdAtTo: to.toISOString(),
    },
  }
}

/** Build the daily sparkline series + duration/count summary from query results */
export function buildWebHitListSummary(
  statsRow: WebsiteHitRow | undefined,
  dailyRows: WebsiteHitRow[],
  from: Date,
  to: Date,
): WebHitListSummary {
  return {
    totalRows: statsRow?.count ?? 0,
    durationMinMs: statsRow?.durationMin,
    durationMaxMs: statsRow?.durationMax,
    durationAvgMs: statsRow?.durationAvg,
    bucketFrom: from,
    dailyBuckets: buildWebHitTimeSeries(dailyRows, from, to, WEB_HIT_DAILY_PERIOD_SEC),
  }
}

/** Format a web hit timestamp for the row list (date + time, in the app's zone) */
export function formatWebHitTimestamp(value: Date | string | undefined): string {
  if (value == null) return ''
  const date = value instanceof Date ? value : new Date(value)
  return date.toLocaleString('en-US', {
    timeZone: WEB_HIT_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}
